import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PermitDepartmentService } from '../../services/permit-department.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { CreatePermitDepartmentPayload, PermitDepartment, PermitDepartmentManager, UpdatePermitDepartmentPayload, permitDepartmentErrorKey } from '../../models/permit-department.model';

// Shape expected by app-assignee-selector (request-v2 shared component).
interface ManagerOption {
    id: string;
    name: string;
    email: string;
    initials: string;
    picture?: string;
}

@Component({
    selector: 'app-permit-department-form',
    templateUrl: './permit-department-form.component.html',
    styleUrls: ['./permit-department-form.component.scss'],
})
export class PermitDepartmentFormComponent implements OnInit, OnDestroy {
    @Input() departmentToEdit: PermitDepartment | null = null;
    @Input() nextOrder = 0;

    @Output() saved = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    form: FormGroup;
    isSaving = false;
    selectedManagerIds: string[] = [];
    preloadedManagers: ManagerOption[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private translate: TranslateService,
        private departmentService: PermitDepartmentService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.initManagers();
    }

    get isEdit(): boolean {
        return !!this.departmentToEdit;
    }

    private initForm(): void {
        const d = this.departmentToEdit;
        this.form = this.fb.group({
            nameFr: [d?.name?.fr || '', Validators.required],
            nameEn: [d?.name?.en || '', Validators.required],
            descriptionFr: [d?.description?.fr || ''],
            descriptionEn: [d?.description?.en || ''],
        });
    }

    private initManagers(): void {
        const managers = this.departmentToEdit?.managers || [];
        this.selectedManagerIds = managers.map((m) => (typeof m === 'string' ? m : m._id)).filter((id) => !!id);
        this.preloadedManagers = managers
            .filter((m): m is PermitDepartmentManager => typeof m !== 'string')
            .map((m) => ({
                id: m._id,
                name: `${m.firstName || ''} ${m.lastName || ''}`.trim(),
                email: m.email || '',
                initials: `${(m.firstName || '?').charAt(0)}${(m.lastName || '').charAt(0)}`.toUpperCase(),
            }));
    }

    onManagersSelect(managers: ManagerOption[]): void {
        this.selectedManagerIds = (managers || []).map((m) => m.id);
    }

    onSave(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid || this.isSaving) return;

        this.isSaving = true;
        const v = this.form.value;
        const hasDescription = !!(v.descriptionFr.trim() || v.descriptionEn.trim());
        const description = hasDescription ? { fr: v.descriptionFr.trim(), en: v.descriptionEn.trim() } : this.departmentToEdit ? { fr: '', en: '' } : undefined;
        const payload: UpdatePermitDepartmentPayload = {
            name: { fr: v.nameFr.trim(), en: v.nameEn.trim() },
            description,
            managers: this.selectedManagerIds,
        };

        const request$ = this.departmentToEdit
            ? this.departmentService.updateDepartment(this.departmentToEdit._id, payload)
            : this.departmentService.createDepartment({ ...payload, order: this.nextOrder } as CreatePermitDepartmentPayload);

        request$.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.isSaving = false;
                const key = this.isEdit ? 'permit.config.departments.form_saved_edit' : 'permit.config.departments.form_saved_create';
                this.messageService.success(this.translate.instant(key));
                this.saved.emit();
            },
            error: (err) => {
                this.isSaving = false;
                this.messageService.error(this.translate.instant(permitDepartmentErrorKey(err)));
            },
        });
    }

    onCancel(): void {
        this.cancel.emit();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
